import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Container, Spinner } from 'react-bootstrap';
import { resolveRuntimeUrl } from 'services/api';
import { getActiveCalls } from 'services/chamadas';
import { medicos, salas } from 'utils/agenda';
import { abreviarNomePaciente } from 'utils/helpers';
import { CardSala } from './CardSala';

function normalizeCall(call) {
  return {
    id: call.id || call.ID,
    nomepac: abreviarNomePaciente(call.NOMEPAC || call.nomepac),
    sala: call.SALA ?? call.sala ?? 'Sala não informada',
    NOMEMEDICO: call.CODINOME || call.NOMEMEDICO || 'Médico não informado',
    calledAt: call.CALLED_AT || call.calledAt || new Date().toISOString(),
    status: call.STATUS || call.status || 'calling',
    isRecalled: Boolean(call.isRecalled),
  };
}

function normalizeRoomIdentifier(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

export default function PainelDeChamadas() {
  const SSE_STALE_MS = 35000;
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [connected, setConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Ref para garantir um ÚNICO AudioContext durante toda a vida da aplicação
  const audioContextRef = useRef(null);
  const lastSseEventAtRef = useRef(0);

  // Inicializa a instância Singleton do AudioContext
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
      if (AudioContextConstructor) {
        audioContextRef.current = new AudioContextConstructor();
      }
    }
    return audioContextRef.current;
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), 1000);
    return () => {
      window.clearInterval(intervalId);
      // Fecha o AudioContext ao desmontar a tela para liberar hardware
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  const formattedDate = useMemo(() => {
    const str = new Intl.DateTimeFormat('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(now);

    return str.charAt(0).toUpperCase() + str.slice(1);
  }, [now]);

  const formattedTime = useMemo(
    () =>
      new Intl.DateTimeFormat('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(now),
    [now]
  );

  const playBeepSequence = useCallback((audioContext) => {
    if (!audioContext || audioContext.state !== 'running') return;

    try {
      const beep = (startAt, frequency) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, startAt);
        gainNode.gain.setValueAtTime(0.0001, startAt);
        gainNode.gain.exponentialRampToValueAtTime(0.4, startAt + 0.04);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.4);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.start(startAt);
        oscillator.stop(startAt + 0.4);
      };

      const nowTime = audioContext.currentTime + 0.01;
      beep(nowTime, 880);
      beep(nowTime + 0.22, 1046.5);
    } catch (e) {
      console.warn('[calling-panel] erro ao emitir som:', e);
    }
  }, []);

  const enableAudio = async () => {
    const audioContext = getAudioContext();
    if (!audioContext) return;

    try {
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      playBeepSequence(audioContext);
      setAudioEnabled(true);
    } catch (error) {
      console.warn('[calling-panel] falha ao ativar áudio:', error);
      setAudioEnabled(false);
    }
  };

  const playAlert = useCallback(async () => {
    const audioContext = getAudioContext();
    if (!audioContext) return;

    if (audioContext.state === 'suspended') {
      try {
        await audioContext.resume();
      } catch (error) {
        setAudioEnabled(false);
        return;
      }
    }

    if (audioContext.state === 'running') {
      playBeepSequence(audioContext);
      setAudioEnabled(true);
    }
  }, [getAudioContext, playBeepSequence]);

  const upsertCall = useCallback(
    (payload, { recalled = false } = {}) => {
      if (!payload?.id && !payload?.ID) return;

      const normalized = normalizeCall({ ...payload, isRecalled: recalled });
      const newRoomKey = normalizeRoomIdentifier(normalized.sala);

      setCalls((current) => {
        const next = current.filter((item) => {
          if (item.id === normalized.id) return false;
          return normalizeRoomIdentifier(item.sala) !== newRoomKey;
        });

        return [
          {
            ...normalized,
            isRecalled: recalled || current.find((item) => item.id === normalized.id)?.isRecalled,
          },
          ...next,
        ];
      });

      playAlert();
    },
    [playAlert]
  );

  const removeCall = useCallback((payload) => {
    const callId = payload?.id || payload?.ID;
    if (!callId) return;
    setCalls((current) => current.filter((item) => item.id !== callId));
  }, []);

  const cancelCall = useCallback((payload) => {
    const callId = payload?.id || payload?.ID;
    if (!callId) return;
    setCalls((current) => current.filter((item) => item.id !== callId));
  }, []);

  const updateCallStatus = useCallback((payload, status) => {
    const callId = payload?.id || payload?.ID;
    if (!callId) return;

    setCalls((current) =>
      current.map((item) => (item.id === callId ? { ...item, status, isRecalled: false } : item))
    );
  }, []);

  const reconcileActiveCalls = useCallback(async () => {
    try {
      const activeCalls = await getActiveCalls();
      setCalls(activeCalls.map((item) => normalizeCall(item)));
    } catch (error) {
      console.warn('[calling-panel] erro ao sincronizar estado via REST:', error);
    }
  }, []);

  const markSseAlive = useCallback(() => {
    lastSseEventAtRef.current = Date.now();
    setConnected(true);
    setIsReconnecting(false);
  }, []);

  useEffect(() => {
    const apiBase = resolveRuntimeUrl(process.env.REACT_APP_API_URL) || window.location.origin;
    const streamUrl = `${apiBase}/calls/stream`;

    lastSseEventAtRef.current = Date.now();
    const eventSource = new EventSource(streamUrl);

    const staleCheckId = window.setInterval(() => {
      const lastEventAt = lastSseEventAtRef.current;
      const timeSinceLastEvent = Date.now() - lastEventAt;

      if (timeSinceLastEvent > SSE_STALE_MS) {
        setConnected(false);
        setIsReconnecting(true);
      }
    }, 3000);

    const handleReconnection = async () => {
      markSseAlive();
      await reconcileActiveCalls();
    };

    eventSource.onopen = () => {
      handleReconnection();
    };

    eventSource.onerror = () => {
      setConnected(false);
      setIsReconnecting(true);
    };

    // Escuta dos eventos SSE
    eventSource.addEventListener('ping', () => {
      markSseAlive();
    });

    eventSource.addEventListener('call_called', (e) => {
      markSseAlive();
      try {
        upsertCall(JSON.parse(e.data));
      } catch (err) {}
    });

    eventSource.addEventListener('call_recalled', (e) => {
      markSseAlive();
      try {
        upsertCall(JSON.parse(e.data), { recalled: true });
      } catch (err) {}
    });

    // Removendo chamadas do painel (Cancelar, Finalizar, Expirar)
    eventSource.addEventListener('call_canceled', (e) => {
      markSseAlive();
      try {
        removeCall(JSON.parse(e.data));
      } catch (err) {}
    });

    eventSource.addEventListener('call_arrived', (e) => {
      markSseAlive();
      try {
        updateCallStatus(JSON.parse(e.data), 'arrived');
      } catch (err) {}
    });

    eventSource.addEventListener('call_done', (e) => {
      markSseAlive();
      try {
        removeCall(JSON.parse(e.data));
      } catch (err) {}
    });

    eventSource.addEventListener('call_expired', (e) => {
      markSseAlive();
      try {
        removeCall(JSON.parse(e.data));
      } catch (err) {}
    });

    return () => {
      window.clearInterval(staleCheckId);
      eventSource.close();
      setConnected(false);
    };
  }, [markSseAlive, reconcileActiveCalls, removeCall, updateCallStatus, upsertCall]);

  useEffect(() => {
    let active = true;
    async function loadCalls() {
      try {
        const activeCalls = await getActiveCalls();
        if (!active) return;
        setCalls(activeCalls.map((item) => normalizeCall(item)));
      } catch (error) {
        if (active) setCalls([]);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadCalls();
    return () => {
      active = false;
    };
  }, []);

  const activeRoomsGrid = useMemo(() => {
    return salas
      .filter((salaObj) => salaObj.ativa !== false)
      .map((salaObj) => {
        const salaIdStr = String(salaObj.id);
        const salaNomeNorm = normalizeRoomIdentifier(salaObj.nome);

        const activeCall =
          calls.find((call) => {
            const rawRoom = String(call.sala);
            return rawRoom === salaIdStr || normalizeRoomIdentifier(rawRoom) === salaNomeNorm;
          }) || null;

        return {
          id: salaObj.id,
          nomeSala: salaObj.nome,
          call: activeCall,
        };
      });
  }, [calls]);

  return (
    <Container
      fluid
      className="vh-100 p-4 d-flex flex-column justify-content-between overflow-hidden"
      style={{
        backgroundColor: '#f1f5f9',
        color: '#0f172a',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <style>{`
  .grid-tv-layout {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, minmax(0, 1fr));
    grid-gap: 1rem;
    flex-grow: 1;
    min-height: 0; /* Impede o vazamento vertical da grid */
    overflow: hidden;
  }

  .grid-tv-layout > :nth-child(11):last-child {
    grid-column: span 2;
  }

  /* Ajustes gerais de contenção para os cards de sala */
  .grid-tv-layout > * {
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  /* Truncamento e ajuste dinâmico para o nome do paciente */
  .nome-paciente-tv {
    font-size: clamp(1rem, 2.6vw, 3.2rem);
    line-height: 1.1;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* Limita a no máximo 2 linhas */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  }
`}</style>

      {/* Header do Painel */}
      <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom border-2 border-slate-200">
        <div>
          <span
            className="badge bg-emerald-100 text-emerald-800 fw-bold text-uppercase px-2 py-1 mb-1"
            style={{ backgroundColor: 'blue', color: 'white', fontSize: '0.85rem' }}
          >
            <h1
              className="display-5 fw-extrabold mb-0 text-slate-900 tracking-tight"
              style={{ fontWeight: 800 }}
            >
              PREVENCOR MARÍLIA
            </h1>
          </span>

          <div className="text-slate-500 fs-6 fw-medium">{formattedDate}</div>
        </div>

        <div className="d-flex align-items-center gap-3">
          {!audioEnabled && (
            <Button
              variant="warning"
              onClick={enableAudio}
              className="fw-bold px-3 py-2 shadow-sm fs-6 text-dark"
            >
              🔔 Ativar Som
            </Button>
          )}

          <div className="text-end">
            <div
              className="fs-1 fw-bold text-slate-900 font-monospace lh-1"
              style={{ fontSize: '2.75rem', color: '#0f172a' }}
            >
              {formattedTime}
            </div>
            <div className="mt-1">
              <Badge
                pill
                bg={connected ? 'success' : 'warning'}
                text={connected ? 'white' : 'dark'}
                className="px-2 py-1 fs-7 fw-semibold"
              >
                {connected ? '● Sincronizado' : isReconnecting ? 'Reconectando...' : 'Buscando...'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de Salas */}
      {loading ? (
        <div className="d-flex align-items-center justify-content-center flex-grow-1">
          <Spinner animation="border" variant="primary" size="lg" />
        </div>
      ) : (
        <div className="grid-tv-layout">
          {activeRoomsGrid.map(({ id, nomeSala, call }) => {
            // Busca o médico associado à sala atual pelo ID da sala
            const medicoDaSala = medicos.find((m) => Number(m.salaId) === Number(id));

            return (
              <CardSala
                key={id}
                nomeSala={nomeSala}
                call={call}
                medicoTitular={medicoDaSala?.apelido || medicoDaSala?.nome || 'LIVRE'}
              />
            );
          })}
        </div>
      )}
    </Container>
  );
}
